import { FC, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { enqueueSnackbar } from "notistack";

import { Box } from "@mui/material";
import { BrokenImage } from "@mui/icons-material";

import { TrainRecommendationSortColumn } from "@framework/types";
import { ProgressOverlay } from "@framework/mui-page-layout";
import { useApiCall } from "@framework/react-hooks";

import { EmptyPlaceholder } from "../../../../../../components/common/empty-placeholder";
import { DataExportButton } from "../../../../../../components/common/buttons/export-button";
import { RecommendationsTable } from "./table";
import { downloadCsv } from "../../../../../../utils/download-csv";
import { useToFakeUtcISO } from "../../../../../../hooks";
import { formatDateHumanReadable } from "../../../../../../utils";
import { useTrainTables } from "../hooks/use-train-tables";
import { IRecommendationsValues, IRecommendationsResponse } from "./interfaces";

export const Recommendations: FC<{ isActiveTableTab?: boolean }> = props => {
  const { isActiveTableTab } = props;

  const [recommendations, setRecommendations] = useState<IRecommendationsResponse[]>([]);

  const {
    trainId,
    startTimestamp,
    endTimestamp,
    isRecorderMode,
    recorderWindowStart,
    sortColumn,
    sortOrder,
    handleSort,
    canFetch,
  } = useTrainTables({
    isActiveTableTab,
    defaultSortColumn: TrainRecommendationSortColumn.Date,
    onReset: () => setRecommendations([]),
  });

  const { formatMessage } = useIntl();
  const toFakeUtcISO = useToFakeUtcISO();

  const recommendationsId = "pages.scheme.recommendations";
  const headersId = `${recommendationsId}.headers`;
  const noRecommendations = formatMessage({ id: "pages.scheme.recommendations.empty" });

  const { fn: getRecommendations, isLoading } = useApiCall(
    (api, { trainId, startTimestamp, endTimestamp, sortColumn, sortOrder }: IRecommendationsValues) => {
      return api
        .fetchJson({
          url: `/trains/${trainId}/recommendations`,
          method: "GET",
          data: {
            startTimestamp,
            endTimestamp,
            sortColumn,
            sortOrder,
          },
        })
        .then((json: IRecommendationsResponse[] | null) => json ?? []);
    },
    { success: false },
  );

  const applyFakeUtcToRecommendations = (items: IRecommendationsResponse[]): IRecommendationsResponse[] =>
    items.map(item => ({
      ...item,
      Date: toFakeUtcISO(item.Date),
    }));

  const handleExport = (): void => {
    try {
      if (!recommendations || !recommendations.length) {
        return;
      }

      if (trainId) {
        const date = toFakeUtcISO(new Date()).split("T")[0];
        const fileName = `train-recommendations-report-${trainId}-${date}.csv`;
        downloadCsv(
          recommendations.map(r => ({
            rate: r.Rate,
            position: r.Position,
            date: formatDateHumanReadable(r.Date),
            name: r.Recommendation || "-",
            priority: r.Priority || "-",
            departmentName: r.DepartmentName || "-",
            performerName: r.PerformerName || "-",
          })),
          fileName,
          [
            { field: "rate", title: formatMessage({ id: `${headersId}.growthRate` }) },
            { field: "position", title: formatMessage({ id: `${headersId}.position` }) },
            { field: "date", title: formatMessage({ id: `${headersId}.date` }) },
            { field: "name", title: formatMessage({ id: `${headersId}.name` }) },
            { field: "priority", title: formatMessage({ id: `${headersId}.priority` }) },
            { field: "departmentName", title: formatMessage({ id: `${headersId}.department` }) },
            { field: "performerName", title: formatMessage({ id: `${headersId}.performer` }) },
          ],
        );
      }
    } catch (error: unknown) {
      console.error("Error during export:", error);
      enqueueSnackbar(formatMessage({ id: "snackbar.exportError" }), { variant: "error" });
    }
  };

  useEffect(() => {
    if (!canFetch) return;

    void getRecommendations(undefined, {
      trainId,
      startTimestamp,
      endTimestamp,
      ...(isRecorderMode ? {} : { sortColumn, sortOrder }),
    }).then(json => {
      if (!Array.isArray(json)) return;

      if (isRecorderMode) {
        setRecommendations(prev => {
          const converted = applyFakeUtcToRecommendations(json);
          const newItems = converted.filter(
            item => !prev.some(prevItem => prevItem.idDiagnoseDefect === item.idDiagnoseDefect),
          );
          const updated = [...prev, ...newItems];
          const filtered = updated.filter(item => new Date(item.Date) >= new Date(recorderWindowStart));
          return filtered;
        });
      } else {
        setRecommendations(applyFakeUtcToRecommendations(json));
      }
    });
  }, [
    trainId,
    startTimestamp,
    endTimestamp,
    sortColumn,
    sortOrder,
    recorderWindowStart,
    isRecorderMode,
    isActiveTableTab,
  ]);

  return (
    <ProgressOverlay
      isLoading={isLoading}
      wrapperSx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}
    >
      <Box sx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}>
        {!recommendations.length ? (
          <EmptyPlaceholder icon={<BrokenImage />} text={noRecommendations} />
        ) : (
          <>
            <Box sx={{ display: "flex", justifyContent: "flex-end", height: "34px" }}>
              <DataExportButton handleExport={handleExport} />
            </Box>
            <RecommendationsTable
              recommendations={recommendations}
              handleSort={handleSort}
              sortColumn={sortColumn}
              sortOrder={sortOrder}
            />
          </>
        )}
      </Box>
    </ProgressOverlay>
  );
};
